import React from 'react';
import {
  Autocomplete,
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  IconButton,
  Tooltip,
  Typography,
  Checkbox,
} from '@mui/material';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { Search as SearchIcon, PictureAsPdf as PdfIcon, Print as PrintIcon, MailOutline as MailIcon, TableView as ExcelIcon } from '@mui/icons-material';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import PageLayout from '../components/common/PageLayout';
import { get } from '../utils/api';
import ThankYouEmailDialog from '../components/reports/ThankYouEmailDialog';

function formatCurrency(v) {
  const n = Number(v || 0);
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);
}

function formatVariationLabel(v) {
  if (!v) return 'All';
  if (v.display_name && String(v.display_name).trim()) return String(v.display_name).trim();
  const prettify = (val) => String(val || '')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());

  let attrs = v.attributes;
  if (typeof attrs === 'string') {
    try {
      attrs = JSON.parse(attrs);
    } catch {
      attrs = null;
    }
  }

  // Woo-style array: [{ name, option }]
  if (Array.isArray(attrs) && attrs.length > 0) {
    const parts = attrs
      .map((a) => {
        if (!a) return '';
        if (typeof a === 'string') return prettify(a);
        return prettify(a.option || a.name || '');
      })
      .filter(Boolean);
    if (parts.length) return parts.join(' / ');
  }

  // Object-map style: { size: { option: 'Large' } } or { size: 'Large' }
  if (attrs && typeof attrs === 'object') {
    const parts = Object.values(attrs)
      .map((x) => {
        if (!x) return '';
        if (typeof x === 'object') return prettify(x.option || x.name || '');
        return prettify(x);
      })
      .filter(Boolean);
    if (parts.length) return parts.join(' / ');
  }

  if (v.sku) return prettify(v.sku);
  return `Variation #${v.id}`;
}

function fmtDate(d) {
  if (!d) return '-';
  const t = new Date(d);
  if (Number.isNaN(t.getTime())) return '-';
  return t.toLocaleDateString();
}

const todayIso = () => new Date().toISOString().slice(0, 10);
const reportActionIconSx = {
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: 1.5,
};
const DATE_RANGE_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'last_12_months', label: 'Last 12 months' },
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'last_7_days', label: 'Last 7 days' },
  { value: 'last_30_days', label: 'Last 30 days' },
  { value: 'last_90_days', label: 'Last 90 days' },
  { value: 'week_to_date', label: 'Week to date' },
  { value: 'month_to_date', label: 'Month to date' },
  { value: 'custom', label: 'Custom' },
];

function formatLocalDate(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function resolveDateRange(range, customStart, customEnd) {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const out = { start: null, end: null };

  if (range === 'all') return out;
  if (range === 'custom') {
    return { start: customStart || null, end: customEnd || null };
  }
  if (range === 'today') {
    const d = formatLocalDate(today);
    return { start: d, end: d };
  }
  if (range === 'yesterday') {
    const d = new Date(today);
    d.setDate(d.getDate() - 1);
    const iso = formatLocalDate(d);
    return { start: iso, end: iso };
  }
  if (range === 'last_7_days') {
    const s = new Date(today);
    s.setDate(s.getDate() - 6);
    return { start: formatLocalDate(s), end: formatLocalDate(today) };
  }
  if (range === 'last_30_days') {
    const s = new Date(today);
    s.setDate(s.getDate() - 29);
    return { start: formatLocalDate(s), end: formatLocalDate(today) };
  }
  if (range === 'last_90_days') {
    const s = new Date(today);
    s.setDate(s.getDate() - 89);
    return { start: formatLocalDate(s), end: formatLocalDate(today) };
  }
  if (range === 'week_to_date') {
    const s = new Date(today);
    s.setDate(s.getDate() - s.getDay()); // Sunday-based week
    return { start: formatLocalDate(s), end: formatLocalDate(today) };
  }
  if (range === 'month_to_date') {
    const s = new Date(today.getFullYear(), today.getMonth(), 1);
    return { start: formatLocalDate(s), end: formatLocalDate(today) };
  }
  if (range === 'last_12_months') {
    const s = new Date(today);
    s.setFullYear(s.getFullYear() - 1);
    s.setDate(s.getDate() + 1);
    return { start: formatLocalDate(s), end: formatLocalDate(today) };
  }
  return out;
}

export default function SponsorsReportPage() {
  const [searchParams] = useSearchParams();
  const rawScope = (searchParams.get('scope') || '').toLowerCase();
  const scope = rawScope === 'kotilinga' || rawScope === 'temple' ? rawScope : null;
  const scopeLabel =
    scope === 'kotilinga'
      ? 'Kotilinga Sponsors Report'
      : scope === 'temple'
        ? 'Temple Sponsors Report'
        : 'Sponsors Report';

  const [productInput, setProductInput] = React.useState('');
  const [products, setProducts] = React.useState([]);
  const [selectedProduct, setSelectedProduct] = React.useState(null);
  const [variations, setVariations] = React.useState([]);
  const [selectedVariation, setSelectedVariation] = React.useState('all');
  const [dateRange, setDateRange] = React.useState('all');
  const [startDate, setStartDate] = React.useState('');
  const [endDate, setEndDate] = React.useState(todayIso());
  const [nameFilter, setNameFilter] = React.useState('');
  const [statusFilter, setStatusFilter] = React.useState('all');
  const [loading, setLoading] = React.useState(false);
  const [hasSearched, setHasSearched] = React.useState(false);
  const [error, setError] = React.useState('');
  const [rows, setRows] = React.useState([]);
  const [meta, setMeta] = React.useState({
    total: 0,
    totals: { sponsors_count: 0, sponsorship_total: 0, paid_total: 0 },
  });
  const [thankYouOpen, setThankYouOpen] = React.useState(false);
  const [thankYouMessage, setThankYouMessage] = React.useState('');
  const [singleEmailOpen, setSingleEmailOpen] = React.useState(false);
  const [singleEmailConfig, setSingleEmailConfig] = React.useState(null);
  const [productPaymentLink, setProductPaymentLink] = React.useState('');
  const [selectedRecipientKeys, setSelectedRecipientKeys] = React.useState({});

  React.useEffect(() => {
    if (!productInput || productInput.length < 2) {
      setProducts([]);
      return;
    }
    let ignore = false;
    (async () => {
      try {
        const res = await get('/ldb/facets/products', { q: productInput, limit: 50 });
        if (!ignore) {
          const raw = Array.isArray(res?.products) ? res.products : [];
          const seen = new Set();
          const deduped = raw.filter((p) => {
            const key = String(p?.name || '').trim().toLowerCase();
            if (!key) return false;
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
          });
          setProducts(deduped);
        }
      } catch {
        if (!ignore) setProducts([]);
      }
    })();
    return () => { ignore = true; };
  }, [productInput]);

  React.useEffect(() => {
    if (!selectedProduct?.id) {
      setVariations([]);
      setSelectedVariation('all');
      return;
    }
    let ignore = false;
    (async () => {
      try {
        const res = await get('/ldb/facets/variations', { product_id: String(selectedProduct.id), limit: 300 });
        const arr = Array.isArray(res?.variations) ? res.variations : [];
        if (!ignore) {
          setVariations(arr);
          setSelectedVariation('all');
        }
      } catch {
        if (!ignore) {
          setVariations([]);
          setSelectedVariation('all');
        }
      }
    })();
    return () => { ignore = true; };
  }, [selectedProduct?.id]);
  React.useEffect(() => {
    setSelectedRecipientKeys({});
  }, [selectedProduct?.id, selectedVariation, dateRange, startDate, endDate, rows.length]);

  React.useEffect(() => {
    if (!selectedProduct?.id) {
      setProductPaymentLink('');
      return;
    }
    let ignore = false;
    (async () => {
      try {
        const res = await get(`/ldb/products/${selectedProduct.id}`);
        if (!ignore) {
          const link = String(
            res?.product?.product_url ||
            res?.product_url ||
            res?.product?.permalink ||
            res?.permalink ||
            ''
          ).trim();
          setProductPaymentLink(link);
        }
      } catch {
        if (!ignore) setProductPaymentLink('');
      }
    })();
    return () => { ignore = true; };
  }, [selectedProduct?.id]);

  const runSearch = async () => {
    if (!selectedProduct?.id) {
      setError('Select a product first.');
      return;
    }
    setError('');
    setLoading(true);
    setHasSearched(true);
    try {
      const range = resolveDateRange(dateRange, startDate, endDate);
      if (dateRange === 'custom' && (!range.start || !range.end)) {
        setError('Please select both start and end date for Custom range.');
        setLoading(false);
        return;
      }
      const params = {
        product_id: String(selectedProduct.id),
        variation_id: selectedVariation === 'all' ? 'all' : String(selectedVariation),
        status: statusFilter,
        q: nameFilter,
        page: '1',
        per_page: '500',
      };
      if (range.start) params.start = range.start;
      if (range.end) params.end = range.end;
      if (scope) params.scope = scope;
      const res = await get('/ldb/reports/sponsors', params);
      setRows(Array.isArray(res?.data) ? res.data : []);
      setMeta(res?.meta || { total: 0, totals: { sponsors_count: 0, sponsorship_total: 0, paid_total: 0 } });
    } catch (e) {
      setError(e?.message || 'Failed to load sponsors report');
      setRows([]);
      setMeta({ total: 0, totals: { sponsors_count: 0, sponsorship_total: 0, paid_total: 0 } });
    } finally {
      setLoading(false);
    }
  };

  const onPrint = () => {
    window.print();
  };

  const exportHeaders = React.useMemo(() => ([
    'Sponsor',
    'Variation',
    'Sponsorship',
    'Paid',
    'Status',
    'Last Order',
    'Email',
    'Phone',
  ]), []);
  const getExportRows = React.useCallback(() => (
    rows.map((r) => [
      r.sponsor_name || '-',
      r.variation_name || '-',
      formatCurrency(r.sponsorship_amount),
      formatCurrency(r.paid_amount),
      r.payment_status || '-',
      fmtDate(r.last_order_date),
      r.email || '-',
      r.phone || '-',
    ])
  ), [rows]);
  const onPdf = () => {
    const exportRows = getExportRows();
    if (!exportRows.length) return;
    const doc = new jsPDF('landscape', 'pt', 'a4');
    const title = `Sponsors Report - ${selectedProduct?.name || ''}`;
    doc.setFontSize(12);
    doc.text(title, 40, 30);
    autoTable(doc, {
      startY: 45,
      head: [exportHeaders],
      body: exportRows,
      styles: { fontSize: 8 },
    });
    const ts = new Date().toISOString().slice(0, 10);
    doc.save(`sponsors-report-${ts}.pdf`);
  };
  const onExcel = () => {
    const exportRows = getExportRows();
    if (!exportRows.length) return;

    const timestamp = new Date();
    const sheet = XLSX.utils.aoa_to_sheet([
      [`Sponsors Report - ${selectedProduct?.name || ''}`],
      [`Generated: ${timestamp.toLocaleString()}`],
      [`Sponsors: ${meta?.totals?.sponsors_count || 0}`],
      [`Sponsorship Total: ${formatCurrency(meta?.totals?.sponsorship_total || 0)}`],
      [`Paid Total: ${formatCurrency(meta?.totals?.paid_total || 0)}`],
      [],
      exportHeaders,
      ...exportRows,
    ]);

    sheet['!cols'] = exportHeaders.map((header, idx) => {
      const values = exportRows.map((row) => String(row[idx] || ''));
      const maxValueLength = values.reduce((max, value) => Math.max(max, value.length), header.length);
      return { wch: Math.min(Math.max(maxValueLength + 2, 14), 32) };
    });

    const book = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(book, sheet, 'Sponsors Report');
    XLSX.writeFile(book, `sponsors-report-${timestamp.toISOString().slice(0, 10)}.xlsx`);
  };

  const getRecipientKey = React.useCallback((row) => {
    const email = String(row?.email || '').trim().toLowerCase();
    if (email) return `email:${email}:${row?.sponsor_key || ''}:${row?.variation_id || 0}`;
    return `row:${row?.sponsor_key || ''}:${row?.variation_id || 0}`;
  }, []);
  const selectableRows = React.useMemo(() => (
    rows.filter((r) => String(r?.email || '').trim().toLowerCase().includes('@'))
  ), [rows]);
  const allSelected = selectableRows.length > 0 && selectableRows.every((r) => !!selectedRecipientKeys[getRecipientKey(r)]);
  const selectedRows = React.useMemo(() => (
    rows.filter((r) => !!selectedRecipientKeys[getRecipientKey(r)])
  ), [rows, selectedRecipientKeys, getRecipientKey]);
  const selectedRecipients = React.useMemo(() => {
    const out = new Set();
    selectedRows.forEach((r) => {
      const email = String(r?.email || '').trim().toLowerCase();
      if (email && email.includes('@')) out.add(email);
    });
    return Array.from(out);
  }, [selectedRows]);
  const selectedVariationLabel = React.useMemo(() => {
    if (selectedVariation === 'all') return 'All';
    const match = variations.find((v) => String(v.id) === String(selectedVariation));
    return match ? formatVariationLabel(match) : 'Selected Variation';
  }, [selectedVariation, variations]);

  const selectedDateRangeLabel = React.useMemo(() => {
    const opt = DATE_RANGE_OPTIONS.find((o) => o.value === dateRange);
    if (dateRange === 'custom') {
      if (startDate && endDate) return `Custom (${startDate} to ${endDate})`;
      return 'Custom';
    }
    return opt?.label || 'All';
  }, [dateRange, startDate, endDate]);
  const selectedRecipientContexts = React.useMemo(() => {
    const out = {};
    selectedRows.forEach((row) => {
      const email = String(row?.email || '').trim().toLowerCase();
      if (!email || !email.includes('@')) return;
      out[email] = {
        productName: selectedProduct?.name || '',
        variationName: row?.variation_name || selectedVariationLabel,
        dateRangeLabel: selectedDateRangeLabel,
        recipientName: row?.sponsor_name || 'Sponsor',
        sponsorshipAmount: Number(row?.sponsorship_amount || 0),
        paymentsMade: Number(row?.paid_amount || 0),
        paymentLink: productPaymentLink || '',
      };
    });
    return out;
  }, [selectedRows, selectedProduct?.name, selectedVariationLabel, selectedDateRangeLabel, productPaymentLink]);

  const openSingleRecipientDialog = (row) => {
    const email = String(row?.email || '').trim().toLowerCase();
    if (!email) return;
    setSingleEmailConfig({
      recipients: [email],
      context: {
        productName: selectedProduct?.name || '',
        variationName: row?.variation_name || selectedVariationLabel,
        dateRangeLabel: selectedDateRangeLabel,
        recipientName: row?.sponsor_name || 'Sponsor',
        sponsorshipAmount: Number(row?.sponsorship_amount || 0),
        paymentsMade: Number(row?.paid_amount || 0),
        paymentLink: productPaymentLink || '',
      },
    });
    setSingleEmailOpen(true);
  };
  const toggleRowSelection = (row, checked) => {
    const key = getRecipientKey(row);
    setSelectedRecipientKeys((prev) => {
      const next = { ...prev };
      if (checked) next[key] = true;
      else delete next[key];
      return next;
    });
  };
  const toggleSelectAll = (checked) => {
    if (!checked) {
      setSelectedRecipientKeys({});
      return;
    }
    const next = {};
    selectableRows.forEach((r) => { next[getRecipientKey(r)] = true; });
    setSelectedRecipientKeys(next);
  };

  return (
    <PageLayout contentMaxWidth="xl" showHeaderImage={false} showOrgHeader>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>{scopeLabel}</Typography>
        <Typography variant="body2" color="text.secondary">
          Unique sponsors for a selected product/variation with sponsorship and paid totals.
        </Typography>
      </Box>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}>
          <Autocomplete
            sx={{ minWidth: 280 }}
            options={products}
            value={selectedProduct}
            onChange={(_, v) => setSelectedProduct(v)}
            inputValue={productInput}
            onInputChange={(_, v) => setProductInput(v)}
            getOptionLabel={(o) => (o?.name ? `${o.name}` : '')}
            filterOptions={(options, state) => {
              const q = String(state?.inputValue || '').trim().toLowerCase();
              if (!q) return options;
              return options.filter((o) => String(o?.name || '').toLowerCase().includes(q));
            }}
            renderInput={(params) => <TextField {...params} label="Product" size="small" />}
          />
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel>Variation</InputLabel>
            <Select
              label="Variation"
              value={selectedVariation}
              onChange={(e) => setSelectedVariation(e.target.value)}
            >
              <MenuItem value="all">All</MenuItem>
              {variations.map((v) => (
                <MenuItem key={v.id} value={v.id}>{formatVariationLabel(v)}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Date Range</InputLabel>
            <Select label="Date Range" value={dateRange} onChange={(e) => setDateRange(e.target.value)}>
              {DATE_RANGE_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          {dateRange === 'custom' ? (
            <>
              <TextField
                label="Start date"
                type="date"
                size="small"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="End date"
                type="date"
                size="small"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </>
          ) : null}
          <Button variant="contained" startIcon={<SearchIcon />} onClick={runSearch} disabled={loading}>
            Search
          </Button>
        </Stack>
        {error ? <Typography color="error" sx={{ mt: 1 }}>{error}</Typography> : null}
      </Paper>

      <Paper sx={{ p: 2 }}>
        {thankYouMessage ? (
          <Alert severity="success" sx={{ mb: 1.5 }} onClose={() => setThankYouMessage('')}>
            {thankYouMessage}
          </Alert>
        ) : null}
        <Stack
          direction={{ xs: 'column', lg: 'row' }}
          spacing={1.5}
          sx={{ mb: 1 }}
          alignItems={{ lg: 'center' }}
          justifyContent="space-between"
        >
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <Typography variant="body2"><strong>Sponsors:</strong> {meta?.totals?.sponsors_count || 0}</Typography>
            <Typography variant="body2"><strong>Sponsorship Total:</strong> {formatCurrency(meta?.totals?.sponsorship_total || 0)}</Typography>
            <Typography variant="body2"><strong>Paid Total:</strong> {formatCurrency(meta?.totals?.paid_total || 0)}</Typography>
          </Stack>
          {hasSearched ? (
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}>
              <TextField
                label="Name / Email / Phone"
                size="small"
                value={nameFilter}
                onChange={(e) => setNameFilter(e.target.value)}
              />
              <FormControl size="small" sx={{ minWidth: 130 }}>
                <InputLabel>Status</InputLabel>
                <Select label="Status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="paid">Paid</MenuItem>
                  <MenuItem value="partial">Partial</MenuItem>
                  <MenuItem value="unpaid">Unpaid</MenuItem>
                </Select>
              </FormControl>
              <Button variant="contained" startIcon={<SearchIcon />} onClick={runSearch} disabled={loading}>
                Apply
              </Button>
              <Tooltip title="Print report">
                <span>
                  <IconButton
                    color="primary"
                    onClick={onPrint}
                    disabled={!rows.length}
                    sx={reportActionIconSx}
                    aria-label="Print report"
                  >
                    <PrintIcon />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Download PDF">
                <span>
                  <IconButton
                    color="error"
                    onClick={onPdf}
                    disabled={!rows.length}
                    sx={reportActionIconSx}
                    aria-label="Download PDF"
                  >
                    <PdfIcon />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Download Excel">
                <span>
                  <IconButton
                    color="success"
                    onClick={onExcel}
                    disabled={!rows.length}
                    sx={reportActionIconSx}
                    aria-label="Download Excel"
                  >
                    <ExcelIcon />
                  </IconButton>
                </span>
              </Tooltip>
              <Button
                variant="outlined"
                startIcon={<MailIcon />}
                onClick={() => setThankYouOpen(true)}
                disabled={selectedRecipients.length === 0}
              >
                Send Email ({selectedRecipients.length})
              </Button>
            </Stack>
          ) : null}
        </Stack>
        {loading ? (
          <Box sx={{ py: 5, textAlign: 'center' }}><CircularProgress /></Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    size="small"
                    checked={allSelected}
                    indeterminate={!allSelected && Object.keys(selectedRecipientKeys).length > 0}
                    onChange={(e) => toggleSelectAll(e.target.checked)}
                  />
                </TableCell>
                <TableCell>Sponsor</TableCell>
                <TableCell>Variation</TableCell>
                <TableCell align="right">Sponsorship</TableCell>
                <TableCell align="right">Paid</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Last Order</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell align="center">Email</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={`${r.sponsor_key}-${r.variation_id || 0}`}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      size="small"
                      checked={!!selectedRecipientKeys[getRecipientKey(r)]}
                      disabled={!String(r?.email || '').trim().toLowerCase().includes('@')}
                      onChange={(e) => toggleRowSelection(r, e.target.checked)}
                    />
                  </TableCell>
                  <TableCell>
                    {r.customer_id ? (
                      <Typography
                        component={RouterLink}
                        to={`/devotees/${r.customer_id}`}
                        sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                      >
                        {r.sponsor_name || '-'}
                      </Typography>
                    ) : (
                      r.sponsor_name || '-'
                    )}
                  </TableCell>
                  <TableCell>{r.variation_name || '-'}</TableCell>
                  <TableCell align="right">{formatCurrency(r.sponsorship_amount)}</TableCell>
                  <TableCell align="right">{formatCurrency(r.paid_amount)}</TableCell>
                  <TableCell sx={{ textTransform: 'capitalize' }}>{r.payment_status || '-'}</TableCell>
                  <TableCell>{fmtDate(r.last_order_date)}</TableCell>
                  <TableCell>{r.email || '-'}</TableCell>
                  <TableCell>{r.phone || '-'}</TableCell>
                  <TableCell align="center">
                    {r.email ? (
                      <Tooltip title="Send email to this sponsor">
                        <IconButton size="small" onClick={() => openSingleRecipientDialog(r)}>
                          <MailIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ) : '-'}
                  </TableCell>
                </TableRow>
              ))}
              {!rows.length ? (
                <TableRow>
                  <TableCell colSpan={11}>
                    <Typography variant="body2" color="text.secondary">No records found.</Typography>
                  </TableCell>
                </TableRow>
              ) : null}
            </TableBody>
          </Table>
        )}
      </Paper>
      <ThankYouEmailDialog
        open={thankYouOpen}
        onClose={() => setThankYouOpen(false)}
        reportType="sponsors"
        context={{
          productName: selectedProduct?.name || '',
          variationName: selectedVariationLabel,
          dateRangeLabel: selectedDateRangeLabel,
        }}
        recipients={selectedRecipients}
        recipientContexts={selectedRecipientContexts}
        onSent={(res) => {
          const sentCount = Number(res?.recipient_count || selectedRecipients.length);
          const mode = String(res?.delivery_mode || '').toLowerCase() === 'individual' ? 'Individual' : 'BCC';
          setThankYouMessage(`Email sent successfully (${mode}). Recipients: ${sentCount}.`);
          setSelectedRecipientKeys({});
        }}
      />
      <ThankYouEmailDialog
        open={singleEmailOpen}
        onClose={() => { setSingleEmailOpen(false); setSingleEmailConfig(null); }}
        reportType="sponsors"
        initialEmailType="payment_reminder"
        context={singleEmailConfig?.context || {}}
        recipients={singleEmailConfig?.recipients || []}
        onSent={() => {
          setThankYouMessage('Individual email sent successfully.');
        }}
      />
    </PageLayout>
  );
}
